import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Paginated } from "@/jenesys-types/data/common";

export interface Supplier {
  is_line_items_extraction_disabled: boolean;
  id: string;
}

export interface SupplierWithName extends Supplier {
  name: string;
}

export type Suppliers = Paginated<SupplierWithName>;

export interface LineItemExtractionState {
  suppliers: Suppliers | null;
  isLoading: boolean;
  getSuppliersError: string | null;
  updateSuppliersError: string | null;
  updateSuppliersSuccess: boolean;
}

interface LoadSuppliersPayload {
  clientId: string;
  offset?: number;
  limit?: number;
  search?: string;
}

interface UpdateSuppliersPayload {
  clientId: string;
  suppliers: Supplier[];
}

export type LoadSuppliersAction = PayloadAction<LoadSuppliersPayload>;

export type UpdateSuppliersAction = PayloadAction<UpdateSuppliersPayload>;

export const initialState: LineItemExtractionState = {
  suppliers: null,
  isLoading: false,
  getSuppliersError: null,
  updateSuppliersError: null,
  updateSuppliersSuccess: false
};

const name = "state/features/LineItemExtraction";

export const lineItemExtractionSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadSuppliers: {
      reducer: (
        state: LineItemExtractionState,
        _action: LoadSuppliersAction
      ) => {
        state.isLoading = true;
        state.getSuppliersError = null;
        state.suppliers = null;
      },
      prepare: (
        clientId: string,
        offset: number = 0,
        limit: number = 10,
        search: string = ""
      ) => ({
        payload: { clientId, offset, limit, search }
      })
    },
    loadSuppliersSuccess: {
      reducer: (
        state: LineItemExtractionState,
        action: PayloadAction<Suppliers>
      ) => {
        state.isLoading = false;
        state.suppliers = action.payload;
      },
      prepare: (payload: Suppliers) => ({
        payload
      })
    },
    loadSuppliersFailure: {
      reducer: (
        state: LineItemExtractionState,
        action: PayloadAction<string>
      ) => {
        state.isLoading = false;
        state.getSuppliersError = action.payload;
      },
      prepare: (error: string) => ({
        payload: error
      })
    },
    updateSuppliers: {
      reducer: (
        state: LineItemExtractionState,
        _action: UpdateSuppliersAction
      ) => {
        state.isLoading = true;
        state.updateSuppliersError = null;
        state.updateSuppliersSuccess = false;
      },
      prepare: (payload: UpdateSuppliersPayload) => ({
        payload
      })
    },
    updateSuppliersSuccess: (state: LineItemExtractionState) => {
      state.isLoading = false;
      state.updateSuppliersSuccess = true;
    },
    updateSuppliersFailure: {
      reducer: (
        state: LineItemExtractionState,
        action: PayloadAction<string>
      ) => {
        state.isLoading = false;
        state.updateSuppliersError = action.payload;
        state.updateSuppliersSuccess = false;
      },
      prepare: (error: string) => ({
        payload: error
      })
    }
  }
});

export const {
  loadSuppliers,
  loadSuppliersSuccess,
  loadSuppliersFailure,
  updateSuppliers,
  updateSuppliersSuccess,
  updateSuppliersFailure
} = lineItemExtractionSlice.actions;

export const selectLineItemExtractionState = (state: {
  features: { LineItemExtraction: LineItemExtractionState };
}): LineItemExtractionState => state.features.LineItemExtraction;

export const selectSuppliers = createSelector(
  selectLineItemExtractionState,
  (state) => state.suppliers
);

export const selectIsLoading = createSelector(
  selectLineItemExtractionState,
  (state) => state.isLoading
);

export const selectUpdateSuppliersSuccess = createSelector(
  selectLineItemExtractionState,
  (state) => state.updateSuppliersSuccess
);

export default lineItemExtractionSlice.reducer;
